/**
 * PopupConnectionStatus - Shows connection state to main window
 *
 * Displays a banner when the popup loses connection to the main window,
 * with information about reconnection attempts.
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@ais-ui/ui-core';
import type { PopupConnectionState } from '../usePopupLifecycle';
import { closePopup } from '@ais-ui/popups';

interface PopupConnectionStatusProps {
  connectionState: PopupConnectionState;
}

export const PopupConnectionStatus: React.FC<PopupConnectionStatusProps> = ({
  connectionState,
}) => {
  const { isConnected, isConnecting } = connectionState;

  // Don't show anything if connected
  if (isConnected && !isConnecting) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4 p-6 bg-card border rounded-lg shadow-lg max-w-md text-center">
        {isConnecting ? (
          <>
            <RefreshCw className="h-10 w-10 text-muted-foreground animate-spin" />
            <div>
              <h3 className="text-lg font-semibold">Reconnecting...</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Lost connection to main window. Attempting to reconnect...
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertTriangle className="h-10 w-10 text-destructive" />
            <div>
              <h3 className="text-lg font-semibold">Connection Lost</h3>
              <p className="text-sm text-muted-foreground mt-1">
                The main application window may have been closed or refreshed.
              </p>
            </div>
            <Button variant="outline" onClick={() => closePopup()}>
              Close Window
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
